/* Horizontal scrollers shared by product rails and the film strip.
   Markup: [data-tc-x] root, optional data-tc-pin="<media query>" for pinned mode,
   [data-tc-x-stage] sticky stage, [data-tc-x-track] the row, [data-tc-x-prev]/[data-tc-x-next],
   [data-tc-x-progress] (uses --p), [data-tc-x-count] ("1 / 3").
   Pinned mode: the section holds in place and vertical page scroll moves the row sideways. */
(() => {
  if (window.__tcXInit) return; window.__tcXInit = true;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-tc-x]').forEach((root) => {
    const track = root.querySelector('[data-tc-x-track]');
    if (!track) return;
    const prev = root.querySelector('[data-tc-x-prev]');
    const next = root.querySelector('[data-tc-x-next]');
    const bar = root.querySelector('[data-tc-x-progress]');
    const count = root.querySelector('[data-tc-x-count]');
    const items = () => [...track.children].filter((c) => c.offsetWidth > 0);
    let pinned = false, distance = 0, ticking = false;

    const paint = (p) => {
      p = Math.min(Math.max(p, 0), 1);
      if (bar) bar.style.setProperty('--p', p.toFixed(4));
      const n = items().length;
      const i = n > 1 ? Math.round(p * (n - 1)) : 0;
      if (count) count.textContent = `${i + 1} / ${n}`;
      if (prev) prev.disabled = p <= 0.01;
      if (next) next.disabled = p >= 0.99;
    };

    // Native horizontal scrolling (when not pinned)
    const nativeP = () => {
      const max = track.scrollWidth - track.clientWidth;
      return max > 0 ? track.scrollLeft / max : 0;
    };
    track.addEventListener('scroll', () => { if (!pinned) paint(nativeP()); }, { passive: true });

    // Pinned mode. vh is held steady: phone browsers change innerHeight as the address bar slides,
    // and re-measuring on that mid-scroll made the rail jump and catch.
    let vh = window.innerHeight, lastW = window.innerWidth;
    const lead = () => (parseFloat(root.dataset.tcPinLead) || 0) * vh;
    const pinP = () => {
      const r = root.getBoundingClientRect();
      const L = lead();
      const span = r.height - vh + L;
      return span > 0 ? (L - r.top) / span : 0;
    };
    // Ease in and out so the sideways motion starts and settles gently instead of switching on and off
    const ease = (t) => t * t * (3 - 2 * t);
    const update = () => {
      ticking = false;
      if (!pinned) return;
      const p = Math.min(Math.max(pinP(), 0), 1);
      const e = root.dataset.tcPinLead ? ease(p) : p;
      track.style.transform = `translate3d(${(-e * distance).toFixed(1)}px, 0, 0)`;
      paint(e);
    };
    const measure = () => {
      const q = root.dataset.tcPin;
      // Only pin when there is enough to travel; short rows keep their arrows
      const travel = track.scrollWidth - track.clientWidth;
      pinned = !!q && !reduce && matchMedia(q).matches && travel > 80;
      root.classList.toggle('is-pinning', pinned);
      if (!pinned) { track.style.transform = ''; root.style.removeProperty('--tc-pin-h'); paint(nativeP()); return; }
      const cs = getComputedStyle(track);
      // Measure the untransformed row without a visible flash: scrollWidth ignores transforms
      distance = Math.max(track.scrollWidth - track.clientWidth + parseFloat(cs.paddingRight || 0), 0);
      const h = vh + distance * (parseFloat(root.dataset.tcPinRate) || 1) - lead();
      root.style.setProperty('--tc-pin-h', `${Math.round(Math.max(h, vh + 1))}px`);
      update();
    };
    window.addEventListener('scroll', () => { if (pinned && !ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    let rt; window.addEventListener('resize', () => {
      // Ignore height-only changes on touch screens (address bar); re-measure on real resizes
      const touch = matchMedia('(hover: none)').matches;
      if (touch && window.innerWidth === lastW) return;
      lastW = window.innerWidth; vh = window.innerHeight;
      clearTimeout(rt); rt = setTimeout(measure, 150);
    });
    window.addEventListener('load', measure);
    // Card sizes can change without a window resize (the film strip sizes its reels from the header height,
    // which changes as the header settles), so re-measure whenever the first card changes width.
    if ('ResizeObserver' in window && track.firstElementChild) {
      let cardW = track.firstElementChild.offsetWidth;
      new ResizeObserver(() => {
        const w = track.firstElementChild ? track.firstElementChild.offsetWidth : 0;
        if (Math.abs(w - cardW) < 1) return;
        cardW = w; clearTimeout(rt); rt = setTimeout(measure, 120);
      }).observe(track.firstElementChild);
    }
    measure();

    // Arrows
    const stepTo = (dir) => {
      const list = items(); const n = list.length;
      if (pinned) {
        const cur = n > 1 ? Math.round(Math.min(Math.max(pinP(), 0), 1) * (n - 1)) : 0;
        const target = Math.min(Math.max(cur + dir, 0), n - 1);
        const p = n > 1 ? target / (n - 1) : 0;
        const top = root.getBoundingClientRect().top + window.scrollY + p * (root.offsetHeight - window.innerHeight);
        window.scrollTo({ top: Math.round(top) + 1, behavior: reduce ? 'auto' : 'smooth' });
      } else {
        // Jump to the exact start of the next or previous card. Scroll snapping is paused during the move,
        // because Safari otherwise snaps a smooth programmatic scroll back to the card it started from.
        const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        const starts = list.map((el) => el.offsetLeft - pad);
        const max = track.scrollWidth - track.clientWidth;
        const cur = track.scrollLeft;
        let target = dir > 0 ? starts.find((x) => x > cur + 4) : [...starts].reverse().find((x) => x < cur - 4);
        if (target === undefined) target = dir > 0 ? max : 0;
        target = Math.min(Math.max(target, 0), max);
        track.style.scrollSnapType = 'none';
        track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
        clearTimeout(track.__tcSnap);
        const restore = () => { track.style.scrollSnapType = ''; };
        if ('onscrollend' in window) track.addEventListener('scrollend', restore, { once: true });
        track.__tcSnap = setTimeout(restore, 900);
      }
    };
    prev && prev.addEventListener('click', () => stepTo(-1));
    next && next.addEventListener('click', () => stepTo(1));
  });
})();
